import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';

interface WalletTOTPVerificationProps {
    email: string;
    idToken: string;
    onVerify: () => void;
    onCancel: () => void;
}

const WalletTOTPVerification: React.FC<WalletTOTPVerificationProps> = ({
    email,
    idToken,
    onVerify,
    onCancel
}) => {
    const [code, setCode] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [isBackupMode, setIsBackupMode] = useState<boolean>(false);

    const handleVerify = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        if (!isBackupMode && code.length !== 6) {
            setError('Please enter a 6-digit code');
            return;
        }
        if (isBackupMode && code.length < 8) {
            setError('Please enter a valid backup code');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axios.post('/pool/api/totp/verify', {
                email,
                token: code,
                isBackupCode: isBackupMode
            }, {
                headers: { Authorization: `Bearer ${idToken}` }
            });

            if (response.data.success) {
                onVerify();
            } else {
                setError(response.data.message || (isBackupMode ? 'Invalid backup code' : 'Invalid TOTP code'));
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Verification failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white dark:bg-slate-800 rounded-3xl sm:rounded-[2.5rem] p-6 sm:p-8 shadow-2xl border border-slate-200/50 dark:border-slate-700/50 text-center relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>

            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 rounded-3xl flex items-center justify-center mx-auto mb-5 sm:mb-6 text-white shadow-lg shadow-blue-500/30">
                <FontAwesomeIcon icon={faShieldHalved} className="text-xl sm:text-2xl" />
            </div>

            <h3 className="text-lg sm:text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight mb-2">
                {isBackupMode ? 'Backup Verification' : 'Security Check'}
            </h3>

            <p className="text-slate-500 dark:text-slate-400 text-[10px] sm:text-[11px] font-bold uppercase tracking-widest mb-6 sm:mb-8 max-w-xs mx-auto leading-relaxed px-2">
                {isBackupMode
                    ? 'Enter one of your 8-digit backup codes to proceed.'
                    : <span>Enter the 6-digit code from your authenticator app for <span className="text-slate-800 dark:text-white break-all">{email}</span></span>}
            </p>

            <form onSubmit={handleVerify}>
                <div className="mb-5 sm:mb-6 relative">
                    <input
                        type="text"
                        value={code}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (isBackupMode) {
                                setCode(val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12));
                            } else {
                                setCode(val.replace(/\D/g, '').slice(0, 6));
                            }
                        }}
                        className={`w-full py-4 sm:py-5 text-center ${isBackupMode ? 'text-lg sm:text-xl tracking-widest' : 'text-2xl sm:text-3xl font-black tracking-[0.3em] sm:tracking-[0.5em]'} rounded-xl sm:rounded-2xl bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-white border-2 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-slate-300 dark:placeholder:text-slate-600 font-mono`}
                        placeholder={isBackupMode ? "BACKUP-CODE" : "000000"}
                        autoFocus
                    />
                </div>

                {error && (
                    <div className="mb-5 sm:mb-6 p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/50 text-rose-600 dark:text-rose-400 rounded-xl text-[10px] sm:text-xs font-bold uppercase tracking-wide animate-pulse">
                        {error}
                    </div>
                )}

                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex-1 py-4 sm:py-4 bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 rounded-xl sm:rounded-2xl font-bold uppercase tracking-widest text-[10px] hover:bg-slate-200 dark:hover:bg-slate-700 transition-all active:scale-95"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading || (isBackupMode ? code.length < 8 : code.length !== 6)}
                        className="flex-[2] py-3.5 sm:py-4 bg-gradient-to-r from-[#0A3A6B] to-blue-600 dark:from-blue-600 dark:to-blue-700 text-white rounded-xl sm:rounded-2xl font-bold uppercase tracking-widest text-[10px] hover:opacity-90 disabled:opacity-50 transition-all shadow-lg shadow-blue-500/30 active:scale-95 flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <>
                                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                Verifying...
                            </>
                        ) : (
                            'Verify & Save'
                        )}
                    </button>
                </div>
            </form>

            <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-slate-100 dark:border-slate-700/50">
                <button
                    type="button"
                    onClick={() => {
                        setIsBackupMode(!isBackupMode);
                        setCode('');
                        setError('');
                    }}
                    className="text-slate-400 dark:text-slate-500 hover:text-blue-500 dark:hover:text-blue-400 text-[10px] font-bold uppercase tracking-widest transition-colors"
                >
                    {isBackupMode ? 'Use Authenticator App' : 'Use Backup Code Instead'}
                </button>
            </div>
        </div>
    );
};

export default WalletTOTPVerification;
